import { Fragment } from 'react'
import type { ReactNode } from 'react'
import { ExternalComponent, Style } from '@sitecore-feaas/sdk'
import { PickerProps } from '../../picker/Picker.js'
import { DialogMenu } from './DialogMenu.js'
import { Divider, HStack } from '@chakra-ui/react'
import { Text } from '@chakra-ui/react'
import { titleize } from 'inflection'
import { getSchemaProperties } from '@sitecore/byoc'

/**
 * Safely formats a value for display in the Configuration panel.
 * Handles all types including objects, arrays, primitives, null, and undefined.
 */
function formatValueForDisplay(value: any, definition?: any): string {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return ''
  }

  // Handle arrays - show count
  if (Array.isArray(value)) {
    return `[${value.length} item${value.length !== 1 ? 's' : ''}]`
  }

  // Handle objects - show placeholder (should not render object directly as React child)
  if (typeof value === 'object') {
    return '[Object]'
  }

  // Handle booleans - show true/false explicitly
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false'
  }
  // Handle numbers and strings - convert to string
  return String(value)
}

/**
 * Determines what value to display for a schema property.
 * Returns the formatted string value or empty string if value should not be shown inline.
 */
function getDisplayValue(definition: any, value: any): string {
  // If definition has nested object properties, we'll recurse - don't show inline value
  if (definition.properties) {
    return ''
  }

  // For arrays (including arrays of objects), show the count
  if (definition.type === 'array') {
    if (Array.isArray(value)) {
      return `[${value.length} item${value.length !== 1 ? 's' : ''}]`
    }
    // Value might be undefined/null or not an array
    return value !== undefined && value !== null ? formatValueForDisplay(value, definition) : ''
  }

  // For all other types (string, number, boolean, etc.), format the value
  return formatValueForDisplay(value, definition)
}

/**
 * Recursive function to render schema properties and their values.
 * Handles nested objects, arrays, and primitive types safely.
 */
function renderProperties(schema: any, values: any, level = 0): ReactNode {
  if (!schema?.properties) return null

  return Object.keys(schema.properties).map((key) => {
    const definition = schema.properties[key]
    const value = values?.[key]
    const padding = level * 2
    const displayValue = getDisplayValue(definition, value)

    return (
      <Fragment key={`${key}-${level}`}>
        <HStack align='flex-start'>
          <Text
            width='50%'
            flexShrink={0}
            flexBasis={'50%'}
            color='blackAlpha.500'
            fontSize='12px'
            noOfLines={3}
            pl={padding}
          >
            {definition.title || titleize(key)}
          </Text>
          <Text
            size='sm'
            variant='solid'
            flexGrow={1}
            noOfLines={3}
            color='blackAlpha.800'
            fontWeight={400}
            fontSize='12px'
          >
            {displayValue}
          </Text>
        </HStack>
        {/* Recurse into nested object properties */}
        {definition.properties && renderProperties(definition, value, level + 1)}
      </Fragment>
    )
  })
}

export default function DialogMenuAttributes({
  setDialogName,
  variant,
  options,
  externalComponent
}: {
  setDialogName: (dialog: string) => void
  variant: PickerProps['variant']
  options: any
  externalComponent: ExternalComponent
}) {
  const keys = Object.keys(externalComponent.schema.properties || {})
    .sort((a, b) => Number(!!options[b]) - Number(!!options[a]))
    .filter((k) => !externalComponent.schema.properties[k]?.isHidden)
  const values = getSchemaProperties(externalComponent.schema, options)

  return (
    <DialogMenu
      title='Configuration'
      variant={variant}
      onNavigate={() => setDialogName('schema')}
      items={[
        {
          onNavigate: () => setDialogName('schema'),
          id: 'schema',
          label: null,
          icon: null,
          preview: () => {
            return (
              <>
                {renderProperties(externalComponent.schema, values)}
                {keys.length > 3 && (
                  <>
                    <Divider orientation='horizontal' />
                    <Text
                      size='sm'
                      variant='solid'
                      flexGrow={1}
                      noOfLines={3}
                      color='blackAlpha.400'
                      fontWeight={400}
                      fontSize='12px'
                    >
                      Has {keys.length - 3} more options
                    </Text>
                  </>
                )}
              </>
            )
          },

          badge: () => {
            return (
              <Text fontWeight={400} fontSize='11px'>
                Update options
              </Text>
            )
          }
        }
      ]}
    />
  )
}
